import { TargetsForm } from "../../components/workcap/Targets";
import React, { useCallback, useEffect, useState } from "react";
import { Loader2, Play, ShieldCheck, Eye, CheckCircle2, X } from "lucide-react";
import { SectionHeader } from "../../components/Primitives";
import PolicyRulesPanel from "../../components/classification/PolicyRulesPanel";
import SettingsEditor from "../../components/classification/admin/SettingsEditor";
import LinksManager from "../../components/classification/admin/LinksManager";
import InstallBasePanel from "../../components/classification/admin/InstallBasePanel";
import ClassificationHistory from "../../components/classification/admin/ClassificationHistory";
import ActualDataPanel from "../../components/classification/admin/ActualDataPanel";
import { relTime } from "../../components/apikeys/shared";
import { OriginSummary, OriginBadge } from "../../lib/origin.jsx";
import { useRole } from "../../lib/role.jsx";
import { getSettings, getHistory, getRules, runPipeline, canEditRules } from "../../lib/classificationApi";

function RunStrip({ run }) {
  if (!run) return null;
  const mode = run.data?.mode || "sample";
  return (
    <div data-testid="rules-run-strip" className="tile px-4 py-2.5 flex flex-wrap items-center gap-x-5 gap-y-1 text-[11px] text-muted">
      <span className="font-semibold uppercase tracking-widest text-[10px] text-fg/80">Latest pipeline run</span>
      <span className="font-mono-tabular text-fg" data-testid="rules-run-id">{run.run_id}</span>
      <span>{relTime(run.ran_at)} · {mode === "practice" ? run.trigger.replace("source:actual", "source:practice") : run.trigger} · {run.duration_ms} ms</span>
      <span data-testid="rules-run-data">Data: {mode === "practice" ? "practice files (not real data)" : mode === "actual" ? "1Rotary actual" : "sample"}</span>
      <span>{run.stats.skus_classified.toLocaleString()} SKUs · window {run.window.start} → {run.window.end}</span>
      <span className="inline-flex items-center gap-1.5 ml-auto">Weather <OriginBadge origin={run.weather.origin} source={run.weather.source} updatedAt={run.weather.fetched_at} testid="rules-weather-origin" /></span>
    </div>
  );
}

export default function ClassificationRules() {
  const { user } = useRole();
  const canEdit = canEditRules(user);
  const [settings, setSettings] = useState(null);
  const [events, setEvents] = useState(null);
  const [rules, setRules] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, h, r] = await Promise.all([getSettings(), getHistory(150), getRules()]);
      setSettings(s.settings); setEvents(h.events); setRules(r); setError(null);
    } catch (e) { setError(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onChanged = (res, msg) => {
    const delta = Object.entries(res?.cells_delta || {}).map(([c, [a, b]]) => `${c} ${a}→${b}`).join(" · ");
    setNotice({ msg, run: res?.run?.run_id, delta });
    load();
  };
  async function rerun() {
    setRunning(true);
    try { const run = await runPipeline(user); setNotice({ msg: "Pipeline re-run on demand", run: run.run_id, delta: "" }); await load(); }
    catch (e) { setError(e.message); }
    setRunning(false);
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="classification-rules-page">
      <SectionHeader eyebrow="Admin Console · AI Governance" title="Classification Rules"
        description="Settings, part replacement links and install base behind the ABC × XYZ matrix. Every change needs a written reason, is recorded in the history and re-runs the pipeline."
        action={canEdit && (
          <button data-testid="rules-run-now" onClick={rerun} disabled={running}
                  className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2 text-[12px] font-semibold bg-primary text-primary-fg rounded-full hover:opacity-90 disabled:opacity-40">
            {running ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />} {running ? "Running…" : "Re-run pipeline now"}
          </button>
        )} />
      <OriginSummary variant="system" />
      <div data-testid="rules-access" className={canEdit ? "text-[11.5px] border border-success/30 bg-success/5 text-success rounded-sm px-3 py-2 inline-flex items-center gap-1.5"
                                                       : "text-[11.5px] border border-info/30 bg-info/5 text-info rounded-sm px-3 py-2 inline-flex items-center gap-1.5"}>
        {canEdit ? <><ShieldCheck size={13} /> {user.roleLabel} · you can edit. Changes apply network-wide and are recorded with your reason.</>
                 : <><Eye size={13} /> Read-only · only the IT Head or COO can change classification rules.</>}
      </div>
      {notice && (
        <div data-testid="rules-notice" className="text-[11.5px] border border-success/30 bg-success/5 rounded-sm px-3 py-2 flex items-start gap-2 animate-fade-in">
          <CheckCircle2 size={13} className="text-success mt-0.5 shrink-0" />
          <span><strong>{notice.msg}.</strong> Pipeline re-ran <span className="font-mono-tabular">{notice.run}</span>{notice.delta ? ` · cells changed: ${notice.delta}` : " · no cell counts changed"}.</span>
          <button data-testid="rules-notice-close" onClick={() => setNotice(null)} className="ml-auto"><X size={12} /></button>
        </div>
      )}
      {error && <div data-testid="rules-error" className="text-[12px] text-critical border border-critical/30 bg-critical/5 rounded-sm p-3">{error}</div>}
      {!settings && !error && <div className="flex items-center gap-2 text-muted text-sm"><Loader2 size={14} className="animate-spin" /> Loading rules…</div>}
      {settings && (
        <>
          <RunStrip run={rules?.run} />
          <ActualDataPanel user={user} onChanged={onChanged} />
          <PolicyRulesPanel rules={rules?.rules} run={rules?.run} testid="admin-policy-rules" defaultOpen />
          <SettingsEditor settings={settings} canEdit={canEdit} actor={user} onChanged={onChanged} />
          <LinksManager canEdit={canEdit} actor={user} onChanged={onChanged} />
          <InstallBasePanel canEdit={canEdit} actor={user} onChanged={onChanged} />
          <ClassificationHistory events={events} />
          {["it_head", "coo"].includes(user?.role) && (
            <div className="tile p-4 space-y-3" data-testid="wc-targets-admin">
              <div className="kpi-label">Working-capital targets · DIO, CCC and GMROI</div>
              <div className="text-[11.5px] text-muted">Targets set the green / amber / red status on the CEO, CFO and COO working-capital KPIs. Every change needs a reason and is recorded.</div>
              <TargetsForm />
            </div>)}
        </>
      )}
    </div>
  );
}
